import { useContext } from 'react';

import { ChunkLoaderContext } from '../context/ChunkLoaderContext';
import { useImageTypeMetadataContext } from '../context/ImageTypeMetadataContext';
import { ChunkedImageProperties } from '../types/chunkedImage';
import { ImageTypeMetadata } from '../types/imageTypeMetadata';
import { LengthRange } from '../types/lengthRange';

import useChunkedImageRanges from './use-chunked-image-ranges';

export interface ChunkLoaderClientProps {
	// Actively loading chunks
	chunks: ChunkedImageProperties[];
	// chunks which provide fallback imagery
	backupChunks?: ChunkedImageProperties[];
}

/***
 * Resolves chunk images for a range using the chunk loader. Will not load required chunks, only get image tiles that are required to render
 * @param range range to get chunks for
 * @param scale current visible scale
 * @param imageTypeMetadata metadata of the currently selected image (derived from core)
 * @param overviewResolution resolution of the overview image provided for this DH
 */
const useChunkResolver = (
	imageTypeMetadata: ImageTypeMetadata | undefined,
	range: LengthRange,
	scale: number,
	overviewResolution: number | undefined,
): ChunkLoaderClientProps => {
	const { getChunk } = useContext(ChunkLoaderContext);
	const { isUncropped } = useImageTypeMetadataContext();

	const {
		chunks: allChunks,
		bestResolution: allBestResolution,
		isOverviewOnly,
	} = useChunkedImageRanges(
		range,
		scale,
		[imageTypeMetadata],
		imageTypeMetadata ? { [imageTypeMetadata.id]: overviewResolution } : {},
	);

	const chunks = imageTypeMetadata && !isOverviewOnly[imageTypeMetadata.id] ? allChunks[imageTypeMetadata.id] : [];
	const bestResolution = imageTypeMetadata ? allBestResolution?.[imageTypeMetadata.id] : undefined;

	const bestChunks = imageTypeMetadata && bestResolution ? (chunks?.[bestResolution] ?? []) : [];

	return {
		chunks:
			imageTypeMetadata && bestResolution
				? bestChunks
						.map((current) => getChunk(imageTypeMetadata.id, current, bestResolution, isUncropped))
						.filter((c): c is ChunkedImageProperties => c !== undefined)
				: [],
		// Get all chunks where less than our visible res, and have tiles available
		// TODO filter out chunks where higher resolutions completely overlap the tile, as to not render an uneccesary row
		backupChunks: imageTypeMetadata
			? Object.keys(chunks)
					.map(Number)
					.filter((res) => bestResolution && res < bestResolution)
					.sort((a, b) => b - a)
					.flatMap((currentResolution) =>
						(chunks[currentResolution] ?? [])
							.map((tile) => getChunk(imageTypeMetadata.id, tile, currentResolution, isUncropped))
							.filter((c): c is ChunkedImageProperties => c !== undefined && !!c.url && !!c.range),
					)
			: [],
	};
};
export { useChunkResolver };
